import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { getDateLocale } from '../../i18n';
import { AppHeader } from '../../components/Layout/AppHeader';
import { BottomNav } from '../../components/BottomNav/BottomNav';
import { getPositionsByDate } from '../../services/api';
import { cachePositions, getCachedPositions } from '../../services/db';
import { usePullToRefresh, PullIndicator } from '../../hooks/usePullToRefresh';
import { StatsMap } from '../../components/StatsMap/StatsMap';
import { PageSpinner } from '../../components/PageSpinner/PageSpinner';
import CalendarPicker from '../../components/CalendarPicker/CalendarPicker';
import type { MapMarker } from '../../components/StatsMap/StatsMap';
import {
  resetFactoryGeocodes,
  hydrateFactoryGeocodes,
  ensureFactoryGeocodes,
  getFactoryCoords,
} from '../../services/factoryGeocoding';
import type { Position } from '../../types';
import { isSelfManagedCargo, displayProductNames, productDraftOf } from '../../services/selfManaged';
import { DraftProduct } from '../../components/DraftProduct/DraftProduct';
import './StatsPage.css';

// ── Helpers de fecha ────────────────────────────────────────────────────────
function toDateKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
function toInputDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function statsLocale() {
  return getDateLocale();
}
function formatDateDisplay(d: Date) {
  return d.toLocaleDateString(statsLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

// Lunes de la semana que contiene la fecha dada. JS: Sun=0, Mon=1, ..., Sat=6.
// Por eso si es domingo retrocedemos 6 días, si es otro día retrocedemos
// (day-1) días.
function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function getWeekDates(monday: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}
function formatWeekDisplay(monday: Date): string {
  const sunday = addDays(monday, 6);
  const start = monday.toLocaleDateString(statsLocale(), { day: 'numeric', month: 'long' });
  const end = sunday.toLocaleDateString(statsLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
  return i18n.t('stats.weekRange', { start, end });
}

// ── Persistencia de fecha (compartida con PositionPage) ───────────────────
const SELECTED_DATE_KEY = 'SELECTED_DATE';
// Marca del grupo cuyo modal está abierto: al abrir una carga (→ detalle) y volver
// con atrás, reabrimos ese modal.
const STATS_MODAL_KEY = 'STATS_MODAL_GROUP';
function loadSavedDate(): Date {
  const saved = localStorage.getItem(SELECTED_DATE_KEY);
  if (saved) {
    const d = new Date(saved);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

// ── Parser de controlador desde factoryAdress ─────────────────────────────
// Formato típico: "Calle X 123 (controlador@aldahra.com)"
function parseController(addr: string | undefined): string | undefined {
  if (!addr) return undefined;
  const m = addr.match(/\(\s*(.*?)\s*\)\s*$/);
  return m?.[1];
}
function cleanFactoryAddress(addr: string | undefined): string | undefined {
  if (!addr) return undefined;
  return addr.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

// ── Flujo self-managed ────────────────────────────────────────────────────
// El criterio vive en services/selfManaged para que /positions y /stats no
// puedan divergir.

// En el flujo nuevo, factoryName apunta a una fábrica PLACEHOLDER sin nombre:
// quien identifica la carga es el PROVEEDOR. Devuelve el nombre real o
// undefined si no hay ninguno (lo usa el geocoding, que no debe buscar
// coordenadas de un texto de relleno).
function rawDisplayFactory(p: Position): string | undefined {
  if (isSelfManagedCargo(p)) {
    return (p.supplierName as string | undefined)?.trim() || undefined;
  }
  const f = (p.factoryName as string | undefined)?.trim();
  return f || cleanFactoryAddress(p.factoryAdress as string | undefined) || undefined;
}

// Etiqueta a pintar. UN SOLO helper para toda la pantalla: leyenda, mapa,
// modal, filtros y métricas comparten la misma clave de agrupación, así una
// misma fábrica/proveedor nunca aparece partida en dos grupos.
function displayFactory(p: Position): string {
  return rawDisplayFactory(p)
    ?? (isSelfManagedCargo(p) ? i18n.t('stats.noSupplier') : i18n.t('stats.noFactory'));
}

// Nombre del producto a mostrar. El criterio (catálogo nuevo, y si no el
// clásico salvo que sea el placeholder) vive en services/selfManaged para que
// sea el mismo en todas las pantallas. Van TODOS los productos: con tres ya
// llegaban juntos y se agrupaba por la lista; con dos se perdía el segundo.
function displayProduct(p: Position): string | undefined {
  return displayProductNames(p);
}

// Dirección con la que geocodificar la carga. En self-managed la ubicación
// real la grabó el controlador; factoryAdress es el respaldo (el backend la
// copia ahí al crear, y para admin lleva pegado el "(controlador)").
function geocodeAddress(p: Position): string | undefined {
  if (isSelfManagedCargo(p)) {
    const loc = (p.locationText as string | undefined)?.trim();
    if (loc) return loc;
  }
  return p.factoryAdress as string | undefined;
}

// ── Paleta de colores para los segmentos del donut ────────────────────────
// Cuidadosamente elegida: contraste alto, accesible, primer color verde
// corporativo para mantener identidad de marca.
const PALETTE = [
  '#187236', // verde corporativo
  '#2563EB', // azul royal
  '#F59E0B', // amber
  '#7C3AED', // púrpura
  '#DC2626', // rojo
  '#0891B2', // cyan
  '#DB2777', // rosa
  '#65A30D', // lima
  '#9333EA', // violeta
  '#EA580C', // naranja
  '#0D9488', // teal
  '#CA8A04', // mostaza
];

// ── Tipo de agrupación ────────────────────────────────────────────────────
type GroupKey = 'factory' | 'controller';

interface Group {
  id: string;            // factoryName o email del controlador
  label: string;         // texto a mostrar
  sublabel?: string;     // fábrica o controlador secundario
  planned: number;
  loaded: number;
  weight: number;
  cargos: Position[];
  controllers: Set<string>;
  factories: Set<string>;
  color: string;
  // Todas sus cargas son del flujo nuevo. Decide si el grupo habla de
  // "fábrica" o de "proveedor" en los rótulos.
  allSelfManaged: boolean;
  // Dirección representativa del grupo: usada por el mapa para
  // geocodificar. En grupos por fábrica todas las cargas tienen la misma;
  // en grupos por controlador puede tener varias y cogemos la primera.
  factoryAdress?: string;
}

function buildGroups(positions: Position[], by: GroupKey): Group[] {
  const map = new Map<string, Group>();
  for (const p of positions) {
    const factory = displayFactory(p);
    const controller = parseController(p.factoryAdress as string | undefined) ?? '—';
    const groupKey = by === 'factory' ? factory : controller;
    const groupId = `${by}:${groupKey}`;
    let g = map.get(groupId);
    if (!g) {
      g = {
        id: groupId,
        label: groupKey,
        sublabel: undefined,
        planned: 0,
        loaded: 0,
        weight: 0,
        cargos: [],
        controllers: new Set(),
        factories: new Set(),
        color: '',
        allSelfManaged: true,
      };
      map.set(groupId, g);
    }
    g.planned += Number(p.totalContainers ?? 0);
    g.loaded += Number(p.totalContainerLoaded ?? 0);
    // Peso REAL cargado del día de esta carga = suma de los pesos (net weight)
    // de los contenedores realmente cargados. NO usamos el planificado.
    g.weight += Number(p.totalContainerDetailWeight ?? 0);
    g.cargos.push(p);
    if (controller !== '—') g.controllers.add(controller);
    g.factories.add(factory);
    if (!isSelfManagedCargo(p)) g.allSelfManaged = false;
    // Cogemos la primera dirección no vacía como representativa del
    // grupo para el geocoding del mapa. Las siguientes se ignoran porque
    // dentro de una misma fábrica suelen ser idénticas.
    if (!g.factoryAdress) {
      const addr = geocodeAddress(p);
      if (addr) g.factoryAdress = addr;
    }
  }
  // Orden: mayor % de carga arriba (lo que muestra cada item de la
  // leyenda). Si dos grupos empatan en %, desempatamos por planificados
  // (más grande primero) para que la jerarquía visual sea estable.
  const list = Array.from(map.values()).sort((a, b) => {
    const pctA = a.planned > 0 ? a.loaded / a.planned : 0;
    const pctB = b.planned > 0 ? b.loaded / b.planned : 0;
    if (pctB !== pctA) return pctB - pctA;
    return b.planned - a.planned;
  });
  // Asignar colores tras ordenar (los más grandes obtienen los colores
  // principales de la paleta).
  list.forEach((g, i) => {
    g.color = PALETTE[i % PALETTE.length];
    if (by === 'factory' && g.controllers.size === 1) {
      g.sublabel = Array.from(g.controllers)[0];
    } else if (by === 'controller' && g.factories.size > 0) {
      g.sublabel = Array.from(g.factories).join(' · ');
    }
  });
  return list;
}

// ── Donut SVG con animación de aparición ──────────────────────────────────

const DONUT_SIZE = 240;
const DONUT_STROKE = 28;
const DONUT_RADIUS = DONUT_SIZE / 2 - DONUT_STROKE / 2;
const DONUT_CIRCUM = 2 * Math.PI * DONUT_RADIUS;

function Donut({
  groups,
  totalPlanned,
  totalLoaded,
  highlightId,
  onSegmentTap,
}: {
  groups: Group[];
  totalPlanned: number;
  totalLoaded: number;
  highlightId?: string | null;
  onSegmentTap?: (id: string) => void;
}) {
  const { t } = useTranslation();
  // Animación: tras montar, "rellenamos" los dasharrays. Empezamos con
  // dashOffset igual a la circunferencia (no se ve nada) y lo bajamos a 0.
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const pct = totalPlanned > 0 ? Math.round((totalLoaded / totalPlanned) * 100) : 0;

  // Calculamos los segmentos. Cada uno representa los cargados de su grupo
  // proporcional al total planificado del día.
  let cumulative = 0;
  const segments = totalPlanned > 0 ? groups.map((g) => {
    const fraction = g.loaded / totalPlanned;
    const dash = DONUT_CIRCUM * fraction;
    const offset = -cumulative;
    cumulative += dash;
    return { g, dash, offset };
  }) : [];

  return (
    <div className="stats-donut" role="img" aria-label={`Progreso ${pct}%`}>
      <svg
        width={DONUT_SIZE}
        height={DONUT_SIZE}
        viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
        className="stats-donut__svg"
      >
        <defs>
          <filter id="stats-donut-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.08" />
          </filter>
        </defs>
        {/* Fondo gris claro = lo que falta por cargar */}
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={DONUT_RADIUS}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={DONUT_STROKE}
          filter="url(#stats-donut-shadow)"
        />
        {/* Segmentos coloreados de cada grupo */}
        {segments.map(({ g, dash, offset }) => {
          const isDimmed = highlightId != null && highlightId !== g.id;
          return (
            <circle
              key={g.id}
              cx={DONUT_SIZE / 2}
              cy={DONUT_SIZE / 2}
              r={DONUT_RADIUS}
              fill="none"
              stroke={g.color}
              strokeWidth={DONUT_STROKE}
              strokeDasharray={animated ? `${dash} ${DONUT_CIRCUM - dash}` : `0 ${DONUT_CIRCUM}`}
              strokeDashoffset={offset}
              transform={`rotate(-90 ${DONUT_SIZE / 2} ${DONUT_SIZE / 2})`}
              className={`stats-donut__segment ${isDimmed ? 'stats-donut__segment--dim' : ''}`}
              onClick={() => onSegmentTap?.(g.id)}
              style={{ cursor: onSegmentTap ? 'pointer' : 'default' }}
            >
              <title>{g.label}: {g.loaded}/{g.planned}</title>
            </circle>
          );
        })}
      </svg>
      {/* Texto central */}
      <div className="stats-donut__center">
        <div className="stats-donut__pct">{pct}%</div>
        <div className="stats-donut__counts">{totalLoaded} / {totalPlanned}</div>
        <div className="stats-donut__label">{t('stats.containers')}</div>
      </div>
    </div>
  );
}

// ── Tarjeta de métrica global ─────────────────────────────────────────────
function MetricCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div className="stats-metric" style={color ? { borderTopColor: color } : undefined}>
      <div className="stats-metric__label">{label}</div>
      <div className="stats-metric__value">{value}</div>
      {sub && <div className="stats-metric__sub">{sub}</div>}
    </div>
  );
}

// ── Modal de detalle de un grupo ──────────────────────────────────────────
function GroupDetailModal({ group, by, onClose }: { group: Group; by: GroupKey; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pct = group.planned > 0 ? Math.round((group.loaded / group.planned) * 100) : 0;

  // Bloqueamos el scroll del body mientras el modal está abierto. Si la
  // card es corta no hay scroll en absoluto; si es larga, solo se mueve
  // su contenido interno (gracias a overflow-y:auto + overscroll-behavior:
  // contain). En iOS Safari, `overflow:hidden` no basta: hay que fijar
  // body con position:fixed y compensar el scrollY actual para no dar un
  // salto al cerrar. Restauramos al desmontar.
  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPosition = body.style.position;
    const prevTop = body.style.top;
    const prevWidth = body.style.width;

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    return () => {
      body.style.overflow = prevOverflow;
      body.style.position = prevPosition;
      body.style.top = prevTop;
      body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, []);

  return (
    <div className="stats-modal-overlay" data-no-pull-refresh onClick={onClose}>
      <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stats-modal__head" style={{ background: group.color }}>
          <div className="stats-modal__title">{group.label}</div>
          {group.sublabel && <div className="stats-modal__sub">{group.sublabel}</div>}
          <button className="stats-modal__close" onClick={onClose} aria-label={t('stats.close')}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div className="stats-modal__body">
          <div className="stats-modal__bigpct" style={{ color: group.color }}>{pct}%</div>
          <div className="stats-modal__bigcount">{t('stats.containersCount', { loaded: group.loaded, planned: group.planned })}</div>

          <div className="stats-modal__progress">
            <div className="stats-modal__progress-fill" style={{ width: `${pct}%`, background: group.color }} />
          </div>

          <div className="stats-modal__rows">
            <div className="stats-modal__row">
              <span>{t('stats.planned')}</span><strong>{group.planned}</strong>
            </div>
            <div className="stats-modal__row">
              <span>{t('stats.loaded')}</span><strong>{group.loaded}</strong>
            </div>
            <div className="stats-modal__row">
              <span>{t('stats.pending')}</span><strong>{Math.max(0, group.planned - group.loaded)}</strong>
            </div>
            {group.weight > 0 && (
              <div className="stats-modal__row">
                <span>{t('stats.totalWeight')}</span><strong>{group.weight.toLocaleString(statsLocale())} Tn</strong>
              </div>
            )}
            {by === 'factory' && group.controllers.size > 0 && (
              <div className="stats-modal__row">
                <span>{group.controllers.size === 1 ? t('stats.controllerOne') : t('stats.controllerOther')}</span>
                <strong className="stats-modal__row-multi">
                  {Array.from(group.controllers).join(', ')}
                </strong>
              </div>
            )}
            {by === 'controller' && group.factories.size > 0 && (
              <div className="stats-modal__row">
                {/* Un grupo 100% self-managed no tiene fábricas: tiene proveedores. */}
                <span>{
                  group.allSelfManaged
                    ? (group.factories.size === 1 ? t('stats.supplierOne') : t('stats.supplierOther'))
                    : (group.factories.size === 1 ? t('stats.factoryOne') : t('stats.factoryOther'))
                }</span>
                <strong className="stats-modal__row-multi">
                  {Array.from(group.factories).join(', ')}
                </strong>
              </div>
            )}
          </div>

          <div className="stats-modal__cargos-title">{t('stats.loadsCount', { count: group.cargos.length })}</div>
          <div className="stats-modal__cargos">
            {group.cargos.map((c) => {
              const cPct = c.totalContainers > 0
                ? Math.round((c.totalContainerLoaded / c.totalContainers) * 100)
                : 0;
              // En self-managed, productName es el placeholder "SELF-MANAGED":
              // el producto real llega aparte.
              const cProduct = displayProduct(c);
              // Sin producto, la nota del portal en su lugar (solo se enseña:
              // no agrupa ni cuenta, como en el portal).
              const cDraft = productDraftOf(c);
              return (
                <div
                  className="stats-modal__cargo stats-modal__cargo--clickable"
                  key={c.idCargo}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/detail/${c.idCargo}`, { state: { position: c, date: c.date ? toInputDate(new Date(c.date)) : undefined } })}
                >
                  <div className="stats-modal__cargo-head">
                    <span className="stats-modal__cargo-name">
                      {cProduct ?? (cDraft ? <DraftProduct text={cDraft} /> : (c.bookingPortic as string))}
                    </span>
                    <span className="stats-modal__cargo-pct">{cPct}%</span>
                  </div>
                  {c.bookingPortic && (cProduct || cDraft) && (
                    <div className="stats-modal__cargo-sub">{c.bookingPortic}</div>
                  )}
                  <div className="stats-modal__cargo-bar">
                    <div
                      className="stats-modal__cargo-fill"
                      style={{ width: `${cPct}%`, background: group.color }}
                    />
                  </div>
                  <div className="stats-modal__cargo-foot">
                    {t('stats.containersCount', { loaded: c.totalContainerLoaded, planned: c.totalContainers })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

type ViewMode = 'day' | 'week';
type ChartView = 'donut' | 'map';
// Qué cargas entran en la pantalla. 'all' = lo de siempre (es el valor por
// defecto, para que nadie note el cambio hasta que toque el interruptor).
type Scope = 'all' | 'selfmanaged';

// Flag de módulo (una sola vez por carga de página): si la navegación
// actual es un "reload" del navegador (F5, pull-down nativo de Chrome
// Android, recargar PWA), borramos el cache de coords. Así un refresh
// del navegador equivale al pull-to-refresh interno de la pantalla.
let reloadHandled = false;

// ── Página principal ──────────────────────────────────────────────────────
export function StatsPage() {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(loadSavedDate);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<GroupKey>(() => {
    const saved = localStorage.getItem('STATS_GROUP_BY');
    return saved === 'controller' ? 'controller' : 'factory';
  });
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('STATS_VIEW_MODE');
    return saved === 'week' ? 'week' : 'day';
  });
  const [chartView, setChartView] = useState<ChartView>(() => {
    const saved = localStorage.getItem('STATS_CHART_VIEW');
    return saved === 'map' ? 'map' : 'donut';
  });
  const [scope, setScope] = useState<Scope>(() => {
    const saved = localStorage.getItem('STATS_SCOPE');
    return saved === 'selfmanaged' ? 'selfmanaged' : 'all';
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [modalGroup, setModalGroup] = useState<Group | null>(null);
  // Al volver del detalle (atrás): si veníamos de una carga abierta DESDE el modal
  // de un grupo, reabrimos ese mismo modal. Se lee en el inicializador (antes de
  // efectos) para no perder la marca por el orden de efectos.
  const [restoreGroupId, setRestoreGroupId] = useState<string | null>(
    () => sessionStorage.getItem(STATS_MODAL_KEY) || null
  );
  // Set de groupIds que el usuario ha ocultado en el mapa. Por defecto
  // vacío (todo visible). Al cambiar el set de grupos (cambio de fecha,
  // semana o groupBy) lo reseteamos para no arrastrar IDs muertos.
  const [hiddenMapGroupIds, setHiddenMapGroupIds] = useState<Set<string>>(new Set());
  const [filterOpen, setFilterOpen] = useState(false);

  // Cuando estamos en modo semana, derivamos lunes/domingo de selectedDate.
  // Así el resto del estado (selectedDate persistida) sigue siendo único.
  const monday = useMemo(() => getMonday(selectedDate), [selectedDate]);

  const changeDate = (d: Date) => {
    localStorage.setItem(SELECTED_DATE_KEY, d.toISOString());
    // Vaciamos la lista y marcamos loading al cambiar de fecha/semana para que
    // se muestre el spinner (y no los datos previos) mientras llega lo nuevo.
    setPositions([]);
    setLoading(true);
    setSelectedDate(d);
  };
  const handleDateChange = (value: string) => {
    const [y, m, d] = value.split('-').map(Number);
    changeDate(new Date(y, m - 1, d));
    setShowCalendar(false);
  };
  const setGroupKey = (k: GroupKey) => {
    setGroupBy(k);
    localStorage.setItem('STATS_GROUP_BY', k);
  };
  const setViewModeKey = (m: ViewMode) => {
    setViewMode(m);
    localStorage.setItem('STATS_VIEW_MODE', m);
  };
  const setChartViewKey = (v: ChartView) => {
    setChartView(v);
    localStorage.setItem('STATS_CHART_VIEW', v);
  };
  const setScopeKey = (s: Scope) => {
    setScope(s);
    localStorage.setItem('STATS_SCOPE', s);
  };
  const shiftWeek = (delta: number) => {
    changeDate(addDays(monday, delta * 7));
  };

  // Fetch de un solo día — reutiliza el patrón original (API + caché).
  const fetchDay = useCallback(async (date: Date): Promise<Position[]> => {
    const key = toDateKey(date);
    try {
      const res = await getPositionsByDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const data: Position[] = res.data ?? [];
      await cachePositions(key, data);
      return data;
    } catch {
      const cached = await getCachedPositions(key);
      return cached ?? [];
    }
  }, []);

  // Fetch unificado: un día o una semana completa según viewMode. Las 7
  // peticiones de la semana van en paralelo — cada día ya cachea por su
  // cuenta, así un fallo aislado no rompe el resto.
  const fetchData = useCallback(async (date: Date, mode: ViewMode) => {
    setLoading(true);
    try {
      if (mode === 'day') {
        const data = await fetchDay(date);
        setPositions(data);
      } else {
        const days = getWeekDates(getMonday(date));
        const results = await Promise.all(days.map(fetchDay));
        setPositions(results.flat());
      }
    } finally {
      setLoading(false);
    }
  }, [fetchDay]);

  useEffect(() => { fetchData(selectedDate, viewMode); }, [selectedDate, viewMode, fetchData]);

  // Si la página ha sido recargada por el navegador (F5, pull-down
  // nativo en Android Chrome, recargar PWA), aplicamos la misma regla
  // que el pull-to-refresh: solo limpiamos el cache de coords si la
  // vista activa es el mapa. Si el user está en Donut, el reload simple
  // basta — los datos del donut ya se refetchean en el mount normal y
  // las coords cacheadas se mantienen intactas para cuando vuelva al
  // mapa. Solo una vez por carga de página gracias al flag de módulo.
  useEffect(() => {
    if (reloadHandled) return;
    reloadHandled = true;
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (nav?.type === 'reload' && chartView === 'map') {
        void resetFactoryGeocodes();
      }
    } catch { /* nada accionable */ }
    // chartView se lee del valor inicial leído de localStorage al montar.
    // No queremos reaccionar a cambios posteriores del toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-geocoding en background: en cuanto cargan las positions, lanzamos
  // el lookup de coordenadas de las fábricas únicas SIN ESPERAR a que el
  // user cambie a la vista de mapa. Así, cuando entre al mapa los pins
  // aparecen prácticamente al instante (o al menos llevan ya unos segundos
  // adelantados). El servicio es idempotente: no martillea fábricas ya
  // cacheadas.
  useEffect(() => {
    if (!positions || positions.length === 0) return;
    let cancelled = false;
    (async () => {
      // Conjunto único de fábricas (factoryName + dirección representativa).
      // Se usa el MISMO nombre que pinta la pantalla (proveedor en
      // self-managed) para que la clave del cache de coordenadas coincida con
      // la del marcador. Sin nombre real no se geocodifica: buscar las
      // coordenadas de un "Sin proveedor" solo gastaría peticiones.
      const unique = new Map<string, { factoryName: string; factoryAdress?: string }>();
      for (const p of positions) {
        const name = rawDisplayFactory(p);
        if (!name) continue;
        if (!unique.has(name)) {
          unique.set(name, { factoryName: name, factoryAdress: geocodeAddress(p) });
        }
      }
      const names = Array.from(unique.keys());
      if (names.length === 0) return;
      await hydrateFactoryGeocodes(names);
      if (cancelled) return;
      const todo = Array.from(unique.values()).filter(
        (x) => getFactoryCoords(x.factoryName) === undefined
      );
      if (todo.length === 0) return;
      await ensureFactoryGeocodes(todo);
    })();
    return () => { cancelled = true; };
  }, [positions]);

  // Pull-to-refresh: el usuario desliza hacia abajo y refrescamos la
  // fecha o semana seleccionada. La acción depende de la vista activa:
  //  - Donut: solo refetch de positions (datos de cargas/contenedores).
  //  - Mapa:  refetch de positions + reset del cache de coordenadas, así
  //           el mapa pide de nuevo todas las coords como si fuera la
  //           primera vez (útil para corregir fábricas mal posicionadas).
  // El reset de geocodes es no-op si estamos offline (lo gestiona
  // resetFactoryGeocodes), así que aquí no necesitamos un check extra.
  const pullRefresh = useCallback(async () => {
    if (chartView === 'map') {
      await resetFactoryGeocodes();
    }
    await fetchData(selectedDate, viewMode);
  }, [fetchData, selectedDate, viewMode, chartView]);
  const pullState = usePullToRefresh(pullRefresh);

  // Determinamos si mostrar el toggle de agrupación. Si todos los items
  // pertenecen a un único controlador (caso típico de un controlador real
  // viendo solo sus cargas), no hace falta — solo le servirá la vista por
  // fábrica. Si hay varios controladores, mostramos el toggle.
  // Interruptor self-managed. Solo tiene sentido si en el día/semana conviven
  // los dos flujos: con uno solo, filtrar no separaría nada (y con solo
  // clásicas dejaría la pantalla vacía). Cuando no se muestra, el filtro no
  // se aplica aunque quedara guardado en localStorage de otro día.
  const hasSelfManaged = useMemo(() => positions.some(isSelfManagedCargo), [positions]);
  const hasClassic = useMemo(() => positions.some((p) => !isSelfManagedCargo(p)), [positions]);
  const showScopeToggle = hasSelfManaged && hasClassic;
  const scopeActive = showScopeToggle && scope === 'selfmanaged';

  // A partir de aquí TODA la pantalla trabaja sobre este conjunto: donut,
  // mapa, métricas, leyenda, filtros y modal. Así el interruptor no necesita
  // tocar nada más.
  const visiblePositions = useMemo(
    () => (scopeActive ? positions.filter(isSelfManagedCargo) : positions),
    [positions, scopeActive]
  );

  const allControllers = useMemo(() => {
    const set = new Set<string>();
    for (const p of visiblePositions) {
      const c = parseController(p.factoryAdress as string | undefined);
      if (c) set.add(c);
    }
    return set;
  }, [visiblePositions]);
  const showGroupToggle = allControllers.size > 1;

  const groups = useMemo(
    () => buildGroups(visiblePositions, showGroupToggle ? groupBy : 'factory'),
    [visiblePositions, groupBy, showGroupToggle]
  );

  // Rotular "proveedor" en vez de "fábrica" cuando todo lo que se ve es del
  // flujo nuevo (donde la fábrica es un placeholder sin nombre).
  const supplierWording = groups.length > 0 && groups.every((g) => g.allSelfManaged);

  // Marcadores del mapa. En modo "factory" hay un marcador por fábrica;
  // en modo "controller" hay un marcador por (controlador, fábrica) — un
  // controlador con varias fábricas se ve como varios pins del mismo
  // color. El click siempre abre el modal del grupo correspondiente al
  // toggle activo.
  const effectiveGroupBy: GroupKey = showGroupToggle ? groupBy : 'factory';
  const mapMarkers: MapMarker[] = useMemo(() => {
    if (effectiveGroupBy === 'factory') {
      // Reusamos los grupos por fábrica — un marcador por grupo.
      const fg = buildGroups(visiblePositions, 'factory');
      return fg.map((g) => ({
        id: g.id,
        groupId: g.id,
        factoryName: g.label,
        factoryAdress: g.factoryAdress,
        label: g.label,
        sublabel: g.sublabel,
        planned: g.planned,
        loaded: g.loaded,
        color: g.color,
      }));
    }
    // Modo controlador: por cada controlador, partimos sus cargas en las
    // fábricas físicas en las que opera. Cada (controlador, fábrica) es
    // un pin con el color del controlador, label = email del controlador,
    // sublabel = nombre de la fábrica.
    const cg = buildGroups(visiblePositions, 'controller');
    const out: MapMarker[] = [];
    for (const g of cg) {
      const byFactory = new Map<string, { factory: string; factoryAdress?: string; planned: number; loaded: number }>();
      for (const p of g.cargos) {
        const factory = displayFactory(p);
        const ex = byFactory.get(factory);
        const planned = Number(p.totalContainers ?? 0);
        const loaded = Number(p.totalContainerLoaded ?? 0);
        if (!ex) {
          byFactory.set(factory, {
            factory,
            factoryAdress: geocodeAddress(p),
            planned, loaded,
          });
        } else {
          ex.planned += planned;
          ex.loaded += loaded;
          if (!ex.factoryAdress) ex.factoryAdress = geocodeAddress(p);
        }
      }
      for (const f of byFactory.values()) {
        out.push({
          id: `${g.id}|${f.factory}`,
          groupId: g.id,
          factoryName: f.factory,
          factoryAdress: f.factoryAdress,
          label: g.label,
          sublabel: f.factory,
          planned: f.planned,
          loaded: f.loaded,
          color: g.color,
        });
      }
    }
    return out;
  }, [visiblePositions, effectiveGroupBy]);

  // Para el modal usamos los grupos del toggle activo (los mismos que el
  // donut/leyenda), de forma que el resumen detallado coincida.
  const groupsForModal = useMemo(
    () => buildGroups(visiblePositions, effectiveGroupBy),
    [visiblePositions, effectiveGroupBy]
  );

  // Mantener la marca del grupo abierto mientras el modal esté visible (para
  // reabrirlo al volver del detalle). Se borra al cerrar el modal.
  useEffect(() => {
    if (modalGroup) sessionStorage.setItem(STATS_MODAL_KEY, modalGroup.id);
    else sessionStorage.removeItem(STATS_MODAL_KEY);
  }, [modalGroup]);

  // Al montar tras volver del detalle: cuando los grupos estén listos, reabrir el
  // modal del grupo guardado (y limpiar la marca de restauración).
  useEffect(() => {
    if (!restoreGroupId) return;
    const g = groupsForModal.find((x) => x.id === restoreGroupId)
      ?? groups.find((x) => x.id === restoreGroupId);
    if (g) {
      setModalGroup(g);
      setRestoreGroupId(null);
    }
  }, [restoreGroupId, groups, groupsForModal]);

  // Reset del filtro de mapa cuando cambia el conjunto de grupos: si
  // cambias de fecha o de modo de agrupación, los IDs anteriores ya no
  // existen, así que partimos de "todo visible". Usamos una clave estable
  // basada en los IDs ordenados.
  const groupsForModalKey = useMemo(
    () => groupsForModal.map((g) => g.id).sort().join('|'),
    [groupsForModal]
  );
  useEffect(() => {
    setHiddenMapGroupIds(new Set());
    setFilterOpen(false);
  }, [groupsForModalKey]);

  // Marcadores efectivos para el mapa: aplicamos el filtro del usuario.
  const visibleMapMarkers = useMemo(
    () => mapMarkers.filter((m) => !hiddenMapGroupIds.has(m.groupId)),
    [mapMarkers, hiddenMapGroupIds]
  );

  // Helpers para el panel de filtros.
  const toggleHidden = (id: string) => {
    setHiddenMapGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const showAllInMap = () => setHiddenMapGroupIds(new Set());
  const hideAllInMap = () =>
    setHiddenMapGroupIds(new Set(groupsForModal.map((g) => g.id)));

  const totalPlanned = groups.reduce((acc, g) => acc + g.planned, 0);
  const totalLoaded = groups.reduce((acc, g) => acc + g.loaded, 0);
  const totalWeight = groups.reduce((acc, g) => acc + g.weight, 0);
  const totalCargos = visiblePositions.length;

  const dateStr = toInputDate(selectedDate);
  const isEmpty = !loading && visiblePositions.length === 0;
  // Primera carga sin datos todavía: mostramos el spinner centrado en vez de
  // pintar un donut a cero que parpadearía al llegar los datos.
  const isInitialLoading = loading && positions.length === 0;

  return (
    <div className="stats-page">
      <PullIndicator {...pullState} />
      <AppHeader
        showLogo
        title={t('stats.title')}
        rightContent={
          <button className="date-toggle-btn" onClick={() => setShowCalendar(!showCalendar)} aria-label={t('positions.selectDate')}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
            </svg>
          </button>
        }
      />

      <div className={`linear-progress ${loading ? 'linear-progress--active' : ''}`}>
        <div className="linear-progress__bar" />
      </div>

      {showCalendar && (
        <div className="date-picker-overlay" onClick={() => setShowCalendar(false)}>
          <div className="date-picker-popup" onClick={(e) => e.stopPropagation()}>
            <label className="date-picker-label">{t('positions.selectDate')}</label>
            <CalendarPicker value={dateStr} onChange={handleDateChange} />
          </div>
        </div>
      )}

      {/* Barra de fecha/semana (con chevrons en semana). Va PRIMERO porque
          es el contexto al que se refieren los toggles de abajo. */}
      <div className="stats-date-bar">
        {viewMode === 'day' ? (
          <span>{formatDateDisplay(selectedDate)}</span>
        ) : (
          <>
            <button
              className="stats-week-nav"
              onClick={() => shiftWeek(-1)}
              aria-label={t('stats.prevWeek')}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
              </svg>
            </button>
            <span className="stats-week-range">{formatWeekDisplay(monday)}</span>
            <button
              className="stats-week-nav"
              onClick={() => shiftWeek(1)}
              aria-label={t('stats.nextWeek')}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Una sola fila con los tres toggles: Día/Semana, Fábrica/
          Controlador y Donut/Mapa. El de agrupación solo aparece si hay
          más de un controlador en el set de cargas (mismo criterio que
          ya teníamos). */}
      <div className={`stats-toggles-row ${showScopeToggle && showGroupToggle ? 'stats-toggles-row--wrap' : ''}`}>
        <div className="stats-toggle">
          <button
            className={`stats-toggle__btn ${viewMode === 'day' ? 'stats-toggle__btn--active' : ''}`}
            onClick={() => setViewModeKey('day')}
          >
            {t('stats.day')}
          </button>
          <button
            className={`stats-toggle__btn ${viewMode === 'week' ? 'stats-toggle__btn--active' : ''}`}
            onClick={() => setViewModeKey('week')}
          >
            {t('stats.week')}
          </button>
        </div>
        {/* Solo cuando conviven los dos flujos: si todo es de uno, no hay nada
            que separar. Arranca en "Todas" = lo que se ve hoy. */}
        {showScopeToggle && (
          <div className="stats-toggle">
            <button
              className={`stats-toggle__btn ${scope === 'all' ? 'stats-toggle__btn--active' : ''}`}
              onClick={() => setScopeKey('all')}
            >
              {t('stats.scopeAll')}
            </button>
            <button
              className={`stats-toggle__btn ${scope === 'selfmanaged' ? 'stats-toggle__btn--active' : ''}`}
              onClick={() => setScopeKey('selfmanaged')}
            >
              {t('stats.scopeSelfManaged')}
            </button>
          </div>
        )}
        {showGroupToggle && (
          <div className="stats-toggle">
            <button
              className={`stats-toggle__btn ${groupBy === 'factory' ? 'stats-toggle__btn--active' : ''}`}
              onClick={() => setGroupKey('factory')}
            >
              {supplierWording ? t('stats.supplierOne') : t('stats.factory')}
            </button>
            <button
              className={`stats-toggle__btn ${groupBy === 'controller' ? 'stats-toggle__btn--active' : ''}`}
              onClick={() => setGroupKey('controller')}
            >
              {t('stats.controller')}
            </button>
          </div>
        )}
        <div className="stats-toggle">
          <button
            className={`stats-toggle__btn ${chartView === 'donut' ? 'stats-toggle__btn--active' : ''}`}
            onClick={() => setChartViewKey('donut')}
          >
            {t('stats.donut')}
          </button>
          <button
            className={`stats-toggle__btn ${chartView === 'map' ? 'stats-toggle__btn--active' : ''}`}
            onClick={() => setChartViewKey('map')}
          >
            {t('stats.map')}
          </button>
        </div>
      </div>

      {/* Botón de filtro del mapa: solo en vista Mapa, debajo de la fila
          de toggles. Permite ocultar fábricas/controladores concretos. */}
      {chartView === 'map' && groupsForModal.length > 0 && (
        <div className="stats-filter-bar">
          <button
            type="button"
            className="stats-filter-trigger"
            onClick={() => setFilterOpen((v) => !v)}
            aria-expanded={filterOpen}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
              <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
            </svg>
            <span>
              {t('stats.showCount', {
                shown: groupsForModal.length - hiddenMapGroupIds.size,
                total: groupsForModal.length,
                type: effectiveGroupBy === 'controller'
                  ? t('stats.typeControllers')
                  : (supplierWording ? t('stats.typeSuppliers') : t('stats.typeFactories')),
              })}
            </span>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"
                 style={{ transform: filterOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
            </svg>
          </button>
        </div>
      )}

      {/* Card flotante de filtros. Backdrop transparente para cerrar al
          tocar fuera; la card se anima al aparecer. */}
      {filterOpen && (
        <div className="stats-filter-overlay" onClick={() => setFilterOpen(false)}>
          <div className="stats-filter-card" onClick={(e) => e.stopPropagation()} data-no-pull-refresh>
            <div className="stats-filter-card__head">
              <span className="stats-filter-card__title">
                {t('stats.showOnMap')}
              </span>
              <button
                type="button"
                className="stats-filter-card__close"
                onClick={() => setFilterOpen(false)}
                aria-label={t('stats.close')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="stats-filter-card__actions">
              <button type="button" className="stats-filter-action" onClick={showAllInMap}>{t('stats.all')}</button>
              <button type="button" className="stats-filter-action" onClick={hideAllInMap}>{t('stats.none')}</button>
            </div>
            <div className="stats-filter-card__list">
              {groupsForModal.map((g) => {
                const checked = !hiddenMapGroupIds.has(g.id);
                return (
                  <label key={g.id} className="stats-filter-item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleHidden(g.id)}
                    />
                    <span className="stats-filter-item__dot" style={{ background: g.color }} />
                    <span className="stats-filter-item__label">{g.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="stats-content">
        {isInitialLoading ? (
          <PageSpinner />
        ) : isEmpty ? (
          <div className="stats-empty">
            <svg viewBox="0 0 24 24" fill="#ccc" width="64" height="64">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-4v-4H6v-4h4V6h4v4h4v4z"/>
            </svg>
            <p>{viewMode === 'week' ? t('stats.emptyWeek') : t('stats.emptyDay')}</p>
          </div>
        ) : (
          <>
            {chartView === 'donut' ? (
              <div className="stats-donut-wrap">
                <Donut
                  groups={groups}
                  totalPlanned={totalPlanned}
                  totalLoaded={totalLoaded}
                  highlightId={highlightId}
                  onSegmentTap={(id) => {
                    const g = groups.find((x) => x.id === id);
                    if (g) setModalGroup(g);
                  }}
                />
              </div>
            ) : (
              <StatsMap
                markers={visibleMapMarkers}
                onMarkerTap={(groupId) => {
                  const g = groupsForModal.find((x) => x.id === groupId);
                  if (g) setModalGroup(g);
                }}
              />
            )}

            {/* Métricas globales en grid */}
            <div className="stats-metrics">
              <MetricCard
                label={t('stats.metricLoads')}
                value={totalCargos}
                sub={totalCargos === 1 ? t('stats.loadOne') : t('stats.loadOther')}
                color="#187236"
              />
              <MetricCard
                label={t('stats.metricContainers')}
                value={`${totalLoaded} / ${totalPlanned}`}
                sub={t('stats.pendingCount', { count: Math.max(0, totalPlanned - totalLoaded) })}
                color="#2563EB"
              />
              <MetricCard
                label={supplierWording ? t('stats.metricSuppliers') : t('stats.metricFactories')}
                value={new Set(visiblePositions.map(displayFactory)).size}
                sub={viewMode === 'week' ? t('stats.activeWeek') : t('stats.activeToday')}
                color="#7C3AED"
              />
              {totalWeight > 0 && (
                <MetricCard
                  label={t('stats.metricWeight')}
                  value={totalWeight.toLocaleString(statsLocale(), { maximumFractionDigits: 1 })}
                  sub={t('stats.tons')}
                  color="#F59E0B"
                />
              )}
            </div>

            {/* Leyenda interactiva */}
            <div className="stats-legend">
              <h3 className="stats-legend__title">
                {effectiveGroupBy === 'controller'
                  ? t('stats.byController')
                  : (supplierWording ? t('stats.bySupplier') : t('stats.byFactory'))}
              </h3>
              {groups.map((g) => {
                const pct = g.planned > 0 ? Math.round((g.loaded / g.planned) * 100) : 0;
                return (
                  <button
                    key={g.id}
                    className="stats-legend__item"
                    onMouseEnter={() => setHighlightId(g.id)}
                    onMouseLeave={() => setHighlightId(null)}
                    onClick={() => setModalGroup(g)}
                  >
                    <span className="stats-legend__dot" style={{ background: g.color }} />
                    <span className="stats-legend__main">
                      <span className="stats-legend__label">{g.label}</span>
                      {g.sublabel && (
                        <span className="stats-legend__sub">{g.sublabel}</span>
                      )}
                    </span>
                    <span className="stats-legend__counts">
                      <span className="stats-legend__pct">{pct}%</span>
                      <span className="stats-legend__nums">{g.loaded}/{g.planned}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {modalGroup && (
        <GroupDetailModal
          group={modalGroup}
          by={showGroupToggle ? groupBy : 'factory'}
          onClose={() => setModalGroup(null)}
        />
      )}

      <BottomNav />
    </div>
  );
}
